import { z } from 'zod'

export const accountStatsBreakdownSchema = z.object({
  reservations: z.number(),
  tickets: z.number(),
  guestlists: z.number(),
  tally: z.number(),
  paymentLinks: z.number(),
  leads: z.number(),
  totalSpent: z.number(),
  processingFeeTotal: z.number().optional(),
  userPaidProcessingFeeTotal: z.number().optional(),
  venueAbsorbedProcessingFeeTotal: z.number().optional(),
  ticketProcessingFeeTotal: z.number().optional(),
  tableProcessingFeeTotal: z.number().optional(),
  connectProcessingFeeTotal: z.number().optional(),
  nonConnectProcessingFeeTotal: z.number().optional(),
  paymentProviderProcessingFeeTotal: z.number().optional(),
  paymentProviderApplicationFeeTotal: z.number().optional(),
  processingFees: z.number().optional(),
  lastActivityAt: z.coerce.date().nullable().optional(),
  featuresUsed: z.number().optional(),
  totalCustomers: z.number().optional(),
  totalVisits: z.number().optional(),
  activeVenues: z.number().optional(),
})

export const accountStatsSchema = accountStatsBreakdownSchema.extend({
  healthScore: z.number(),
  score: z.number().optional(),
  computedAt: z.coerce.date(),
})

// Backward-compatible aliases while callers are migrated.
export const accountSummarySchema = accountStatsSchema
export const accountLifetimeStatsSchema = accountStatsSchema

const queryBoolean = z.preprocess((value) => {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const lowered = value.toLowerCase()
    if (lowered === 'true') return true
    if (lowered === 'false') return false
  }
  return value
}, z.boolean())

export const accountSummaryAdminDashboardQuerySchema = z.object({
  subscriptionActive: queryBoolean.optional(),
  topLimit: z.coerce.number().int().min(1).max(50).optional(),
})

const accountSummaryBrandingColorSchema = z.object({
  red: z.number().nullish(),
  green: z.number().nullish(),
  blue: z.number().nullish(),
})

const accountSummaryVenueBrandingSchema = z.object({
  primaryColor: accountSummaryBrandingColorSchema.nullish(),
  secondaryColor: accountSummaryBrandingColorSchema.nullish(),
  theme: z.enum(['light', 'dark']).nullish(),
})

export const accountSummaryAdminVenueRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  organization: z.string(),
  organizationName: z.string().nullish(),
  subscriptionActive: z.boolean().nullish(),
  subscriptionPlan: z.string().nullish(),
  logo: z.string().nullish(),
  branding: accountSummaryVenueBrandingSchema.nullish(),
  stats: accountStatsSchema.nullish(),
  lifetime: accountLifetimeStatsSchema.nullish(),
})

export const accountSummaryAdminOrgRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  subscriptionActive: z.boolean().nullish(),
  activeSubscriptions: z.number().nullish(),
  stats: accountStatsSchema.nullish(),
  lifetime: accountLifetimeStatsSchema.nullish(),
})

export const accountSummaryScoreDistributionSchema = z.object({
  danger: z.number(),
  warning: z.number(),
  success: z.number(),
})

export const accountSummaryFeatureAdoptionRatesSchema = z.object({
  reservations: z.number(),
  tickets: z.number(),
  guestlists: z.number(),
  tally: z.number(),
  paymentLinks: z.number(),
  leads: z.number(),
  totalCustomers: z.number(),
})

export const accountSummaryAdminSummarySchema = z.object({
  venueCount: z.number(),
  orgCount: z.number(),
  averageScore: z.number(),
  medianScore: z.number(),
  atRiskVenueCount: z.number(),
  atRiskOrgCount: z.number(),
  latestComputedAt: z.coerce.date().nullable(),
})

export const accountSummaryAdminDashboardSchema = z.object({
  filters: z.object({
    subscriptionActive: z.boolean(),
    topLimit: z.number(),
  }),
  summary: accountSummaryAdminSummarySchema,
  scoreDistribution: accountSummaryScoreDistributionSchema,
  featureTotals: accountStatsBreakdownSchema.partial(),
  featureAdoptionRates: accountSummaryFeatureAdoptionRatesSchema,
  topVenues: z.array(accountSummaryAdminVenueRowSchema),
  atRiskVenues: z.array(accountSummaryAdminVenueRowSchema),
  topOrgs: z.array(accountSummaryAdminOrgRowSchema),
  atRiskOrgs: z.array(accountSummaryAdminOrgRowSchema),
})

export const accountSummaryAdminDashboardResponseSchema = z.object({
  data: accountSummaryAdminDashboardSchema,
})
